import axios from 'axios';
import useSWR from 'swr';

import { useAppContext } from 'hooks/context/useAppContext';

import { Form, FormStatus, Waiver } from 'types/forms-and-waivers';

type AppointmentFormResponse = {
  data: Form;
};

type TSubmit = {
  questions: {
    guid: string;
    version: number;
    answer: string;
  }[];
  waivers: {
    guid: string;
    version: number;
    accepted: boolean;
  }[];
};

const latestVersionOfWaivers = (acc: Waiver[], waiver: Waiver) => {
  const existingWaiver = acc.find((w) => w.guid === waiver.guid);

  if (!existingWaiver) {
    acc.push(waiver);
  } else if (waiver.version > existingWaiver.version) {
    const index = acc.findIndex((w) => w.guid === waiver.guid);

    acc[index] = waiver;
  }

  return acc;
};

export const useAppointmentForm = (guid: string) => {
  const { config, publicUser } = useAppContext();
  const url = `${config.apiUrlV3}/web/appointments/${guid}/forms?slug=${publicUser.slug}`;

  const { isLoading, error, data, mutate } =
    useSWR<AppointmentFormResponse>(url);

  const result = data?.data;

  const filteredWaivers = result?.waivers?.reduce(
    latestVersionOfWaivers,
    [] as Waiver[],
  );

  if (result?.waivers) result.waivers = filteredWaivers;

  async function submitForm({ questions, waivers }: TSubmit) {
    try {
      await axios.post(url, {
        questions,
        waivers,
      });
    } catch (error) {
      const defaultErrorMessage =
        'Oops, there was an error submitting the form. Please try again.';
      let errorMessage = null;

      if (axios.isAxiosError(error)) {
        if (error.response?.data?.errors[0]?.type === 'empty_form_submission') {
          errorMessage =
            'Your form is empty, please complete at least one question to submit.';
        } else {
          errorMessage =
            error.response?.data?.errors[0]?.full_message ||
            error.response?.data?.errors[0]?.message;
        }
      }

      throw new Error(errorMessage || defaultErrorMessage);
    }

    void mutate({
      data: { ...data, status: FormStatus.submitted },
    });
  }

  return { isLoading, error, data: result, submitForm };
};
